import React, { useEffect, useState } from "react";
import api from "../../api";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";


const RacetrackModal = ({ isOpen, toggle }) => {
  const navigate = useNavigate();
  
  // Store current username from API response
  const [username, setUsername] = useState("");
  // =======================
  // Racetrack & Pokemon Data
  // =======================
  const [racetrackData, setRacetrackData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [myPokemons, setMyPokemons] = useState([]);

  // ===================
  // Modals & Form State
  // ===================
  const [showAdd, setShowAdd] = useState(false);
  const [pokid, setPokid] = useState("");

  const [showChallenge, setShowChallenge] = useState(false);
  const [challengeRaceId, setChallengeRaceId] = useState(null);
  const [challengePokid, setChallengePokid] = useState("");

  // =========================
  // Racing Animation Overlay
  // =========================
  const [showRaceAnimation, setShowRaceAnimation] = useState(false);
  const [racingResult, setRacingResult] = useState(null);
  const [raceMinipicUser, setRaceMinipicUser] = useState("");
  const [raceMinipicTrack, setRaceMinipicTrack] = useState("");

  // =========================
  // Popup Overlay (replaces alerts)
  // =========================
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupColor, setPopupColor] = useState("#1d222e");
  const [popupImg, setPopupImg] = useState(null);

  // =========================

  // =========================
  // Load Racetracks & MyPokemons
  // =========================
  const loadRacetracks = async () => {
    setLoading(true);
    setError("");
    
    try {
      // Get racetracks data
      const res = await api.get("/v1/racetracks", {
        withCredentials: true,
      });
      
      // Set racetrack data
      setRacetrackData(res.data);
      
      // Get current user's profile to identify the username
      const profileRes = await api.get("/v1/profile", {
        withCredentials: true,
      });
      
      if (profileRes.data && profileRes.data.username) {
        console.log('Setting username from profile API:', profileRes.data.username);
        setUsername(profileRes.data.username);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load racetrack data.");
    } finally {
      setLoading(false);
    }
  };

  const loadMyPokemons = async () => {
    try {
      // Only get user's own Pokemon, not orphaned 'na' Pokemon
      const res = await api.get("/v1/my-pokemon?availableForRace=1", {
        withCredentials: true,
      });

      setMyPokemons(res.data);
    } catch (err) {
      console.error("Error loading Pokémon:", err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadRacetracks();
      loadMyPokemons();
    }
  }, [isOpen, username]);

  // =========================
  // Add Pokemon to Track
  // =========================
  const handleAddPokemon = async () => {
    if (!pokid) {
      setPopupColor("red");
      setPopupMessage("Please select a Pokémon!");
      setPopupImg(null);
      setShowPopup(true);
      return;
    }
    try {
      const res = await api.post(
        `/v1/racetracks/add?pokid=${pokid}`,
        {},
        { withCredentials: true },
      );

      // Use regular sprite image instead of custom minipic
      let sprite = null;
      const selectedMon = myPokemons.find((p) => p.id === Number(pokid));
      if (selectedMon) {
        sprite = `/sprites/${selectedMon.name}.png`;
      }

      setPopupColor("#1d222e");
      setPopupMessage(res.data.message || "Pokémon placed successfully!");
      setPopupImg(sprite);
      setShowPopup(true);

      setShowAdd(false);
      setPokid("");
      loadRacetracks();
      loadMyPokemons();
    } catch (err) {
      console.error(err);
      setPopupColor("red");
      setPopupMessage(
        err.response?.data?.detail || "Error adding Pokémon to track.",
      );
      setPopupImg(null);
      setShowPopup(true);
    }
  };

  // =========================
  // Challenge Flow
  // =========================
  const handleChallenge = (raceId) => {
    setChallengeRaceId(raceId);
    setShowChallenge(true);
  };

  const confirmChallenge = async () => {
    if (!challengePokid) return;
    setShowChallenge(false);

    // 1) Fetch track entry
    const trackEntry = racetrackData.find((r) => r.id === challengeRaceId);
    if (!trackEntry) {
      setPopupColor("red");
      setPopupMessage("Track entry not found");
      setPopupImg(null);
      setShowPopup(true);
      return;
    }
    
    // Use regular sprite instead of minipic
    setRaceMinipicTrack(`/sprites/${trackEntry.pokname}.png`);

    // 2) My Pokémon sprite (use regular sprite)
    const selectedMon = myPokemons.find((m) => m.id === parseInt(challengePokid));
    if (selectedMon) {
      setRaceMinipicUser(`/sprites/${selectedMon.name}.png`);
    } else {
      setRaceMinipicUser("/img/placeholder.png");
    }

    // 3) Call server to get race outcome
    try {
      const res = await api.post(
        `/v1/racetracks/${challengeRaceId}/challenge`,
        parseInt(challengePokid),
        { withCredentials: true },
      );
      setRacingResult(res.data);
      setShowRaceAnimation(true);

      // Reload data
      loadRacetracks();
      loadMyPokemons();
    } catch (err) {
      console.error(err);
      setPopupColor("red");
      setPopupMessage(
        err.response?.data?.detail || "Error challenging Pokémon.",
      );
      setPopupImg(null);
      setShowPopup(true);
    }
  };

  // Called after the racing motion finishes
  const handleAnimationComplete = () => {
    // (Optional)
  };

  // Close the racing overlay
  const closeRacingOverlay = () => {
    setShowRaceAnimation(false);
    setRacingResult(null);
    setRaceMinipicUser("");
    setRaceMinipicTrack("");
  };

  // =========================
  // Utility: Format Pokémon Name
  // =========================
  const typedPrefixes = ["Ruby", "Sapphire", "Emerald", "Shadow", "Golden", "Diamond"];
  const formatPokemonName = (name) => {
    for (let prefix of typedPrefixes) {
      if (name.startsWith(prefix)) {
        return prefix + " " + name.slice(prefix.length);
      }
    }
    return name;
  };

  const isColorPokemon = (name) => {
    return typedPrefixes.some((prefix) => name.startsWith(prefix));
  };

  // Extract base Pokemon species name (remove typed prefix)
  const getBasePokemonName = (name) => {
    for (let prefix of typedPrefixes) {
      if (name.startsWith(prefix)) {
        return name.slice(prefix.length);
      }
    }
    return name;
  };

  // Check if user has eligible Pokemon to challenge a specific racetrack entry
  const canChallengeEntry = (entry) => {
    return myPokemons.some((pkm) => 
      isColorPokemon(pkm.name) && 
      pkm.name === entry.pokname
    );
  };
  
  const trackEntry = racetrackData.find((r) => r.id === challengeRaceId);
  const trackName = trackEntry?.pokname || "";
  const trackBaseName = getBasePokemonName(trackName);

  if (!isOpen) return null;

  return (
    <div 
      className="game-popup-overlay"
      onClick={toggle}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        boxSizing: 'border-box',
        zIndex: 1050
      }}
    >
      <div 
        className="racetrack-popup"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '95%',
          maxWidth: 900,
          maxHeight: '95vh',
          background: 'rgba(29, 34, 46, 0.95)',
          color: '#fff',
          borderRadius: 12,
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <h3 style={{ 
            margin: 0, 
            fontSize: '20px', 
            fontWeight: 'bold'
          }}>
            🏁 Racetracks
          </h3>
          <button
            onClick={toggle}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '4px',
              opacity: 0.7
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.7'}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: '20px',
          overflowY: 'auto',
          flex: 1
        }}>
          {/* Race GIF and Description */}
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <img
              src="/Minigames/race.gif"
              alt="Race"
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                borderRadius: '8px',
                marginBottom: '16px'
              }}
            />
            <h4 style={{ marginBottom: '16px' }}>Welcome to the Racetracks!</h4>
          </div>

          {/* Rules */}
          <div style={{ 
            background: 'rgba(0, 0, 0, 0.3)',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px',
            lineHeight: '1.6'
          }}>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Risky Race:</strong> Lose a race? You forfeit your Pokémon to the winner.</li>
              <li><strong>Higher Level Advantage:</strong> Higher-level Pokémon have better odds.</li>
              <li><strong>Typed Pokémon:</strong> (e.g. <em>Ruby, Emerald, Golden</em>) can only race against the same typed prefix.</li>
              <li>In some cases, more than two people might challenge the same Pokémon, resulting in multiple races in quick succession.</li>
            </ul>
            <p style={{ 
              color: '#dc3545', 
              fontWeight: 'bold', 
              margin: '12px 0 0 0' 
            }}>
              Note: Pokémon will not be returned under any circumstances, so race at your own risk!
            </p>
          </div>

          {/* Header with Add Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <h5 style={{ margin: 0 }}>Active Racetrack Entries</h5>
            <button
              onClick={() => setShowAdd(true)}
              style={{
                background: 'linear-gradient(135deg, #007bff, #0056b3)',
                border: 'none',
                color: '#fff',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
            >
              Add Pokémon
            </button>
          </div>

          {/* Loading/Error States */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px', opacity: 0.8 }}>
              Loading racetrack data...
            </div>
          )}
          {error && (
            <div style={{ 
              color: '#dc3545', 
              textAlign: 'center', 
              padding: '20px',
              background: 'rgba(220, 53, 69, 0.1)',
              borderRadius: '8px'
            }}>
              {error}
            </div>
          )}

          {/* Racetrack Table */}
          {!loading && !error && (
            <div style={{
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse' 
              }}>
                <thead>
                  <tr style={{ background: 'rgba(0, 0, 0, 0.5)' }}>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>-</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Pokémon</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Level</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Trainer</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {racetrackData.map((entry, index) => (
                    <tr key={entry.id} style={{
                      borderBottom: index < racetrackData.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none'
                    }}>
                      <td style={{ padding: '12px 8px' }}>
                        <img
                          src={`/sprites/${entry.pokname}.png`}
                          alt=""
                          style={{ 
                            width: '40px', 
                            height: 'auto',
                            imageRendering: 'pixelated'
                          }}
                          onError={(e) => {
                            e.target.src = `/icon/${entry.pokname}.gif`;
                          }}
                        />
                      </td>
                      <td style={{ padding: '12px 8px', fontWeight: 'bold' }}>
                        {formatPokemonName(entry.pokname)}
                      </td>
                      <td style={{ padding: '12px 8px' }}>{entry.level}</td>
                      <td style={{ padding: '12px 8px' }}>
                        <span 
                          onClick={() => {
                            toggle(); // Close the modal
                            navigate(`/playerprofile?user=${entry.owner_name}`); // Navigate to trainer profile
                          }}
                          style={{ 
                            color: '#4dabf7',
                            textDecoration: 'underline',
                            cursor: 'pointer'
                          }}
                          title={`View ${entry.owner_name}'s profile`}
                        >
                          {entry.owner_name}
                        </span>
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        {entry.owner_name && username && entry.owner_name.toLowerCase() === username.toLowerCase() ? (
                          <button
                            disabled
                            style={{
                              background: 'rgba(108, 117, 125, 0.5)',
                              border: 'none',
                              color: 'rgba(255, 255, 255, 0.5)',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'not-allowed'
                            }}
                            title="You cannot race against your own Pokémon"
                          >
                            Own Pokémon
                          </button>
                        ) : !canChallengeEntry(entry) ? (
                          <button
                            disabled
                            style={{
                              background: 'rgba(108, 117, 125, 0.5)',
                              border: 'none',
                              color: 'rgba(255, 255, 255, 0.5)',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'not-allowed'
                            }}
                            title={`You need a ${entry.pokname} to challenge this race`}
                          >
                            No Matching Pokémon
                          </button>
                        ) : (
                          <button
                            onClick={() => handleChallenge(entry.id)}
                            style={{
                              background: 'linear-gradient(135deg, #28a745, #20c997)',
                              border: 'none',
                              color: '#fff',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: 'bold'
                            }}
                          >
                            Race Pokémon
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ADD POKEMON MODAL */}
      {showAdd && (
        <div 
          className="game-popup-overlay"
          onClick={() => setShowAdd(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1060
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(29, 34, 46, 0.95)',
              color: '#fff',
              borderRadius: 12,
              padding: '20px',
              maxWidth: 400,
              width: '90%',
              border: '2px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <h4 style={{ marginBottom: '16px' }}>Add Pokémon to Racetracks</h4>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Pokémon
              </label>
              <select
                value={pokid}
                onChange={(e) => setPokid(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(0, 0, 0, 0.5)',
                  color: '#fff',
                  fontSize: '14px'
                }}
              >
                <option value="">-- Select a Pokémon --</option>
                {myPokemons
                  .filter((pkm) => isColorPokemon(pkm.name))
                  .map((pkm) => (
                    <option key={pkm.id} value={pkm.id}>
                      {formatPokemonName(pkm.name)} Level {pkm.level}
                    </option>
                  ))}
              </select>
            </div>
            <p style={{ 
              fontSize: '12px', 
              opacity: 0.8, 
              marginBottom: '20px',
              color: '#ffc107'
            }}>
              Note: You are risking losing the Pokémon that participates in the race.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAdd(false)}
                style={{
                  background: 'rgba(108, 117, 125, 0.5)',
                  border: 'none',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddPokemon}
                style={{
                  background: 'linear-gradient(135deg, #007bff, #0056b3)',
                  border: 'none',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CHALLENGE MODAL */}
      {showChallenge && (
        <div 
          className="game-popup-overlay"
          onClick={() => setShowChallenge(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1060
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(29, 34, 46, 0.95)',
              color: '#fff',
              borderRadius: 12,
              padding: '20px',
              maxWidth: 400,
              width: '90%',
              border: '2px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <h4 style={{ marginBottom: '16px' }}>Challenge a Pokémon</h4>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Your Pokémon
              </label>
              <select
                value={challengePokid}
                onChange={(e) => setChallengePokid(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(0, 0, 0, 0.5)',
                  color: '#fff',
                  fontSize: '14px'
                }}
              >
                <option value="">-- Select a Pokémon --</option>
                {myPokemons
                  .filter((pkm) => isColorPokemon(pkm.name))
                  .filter((pkm) => pkm.name === trackName)
                  .map((pkm) => (
                    <option key={pkm.id} value={pkm.id}>
                      {formatPokemonName(pkm.name)} Level {pkm.level}
                    </option>
                  ))}
              </select>
            </div>
            <p style={{ 
              fontSize: '12px', 
              opacity: 0.8, 
              marginBottom: '20px',
              color: '#dc3545'
            }}>
              If you lose, you forfeit your Pokémon to the winner.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowChallenge(false)}
                style={{
                  background: 'rgba(108, 117, 125, 0.5)',
                  border: 'none',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmChallenge}
                style={{
                  background: 'linear-gradient(135deg, #dc3545, #c82333)',
                  border: 'none',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Race!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RACING ANIMATION OVERLAY */}
      {showRaceAnimation && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1070
          }}
        >
          <div style={{
            background: 'rgba(29, 34, 46, 0.95)',
            color: '#fff',
            borderRadius: 12,
            padding: '20px',
            maxWidth: 500,
            width: '90%',
            border: '2px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h5 style={{ marginBottom: '20px', textAlign: 'center' }}>The Race is On!</h5>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'end',
                height: 100,
                marginBottom: '20px'
              }}
            >
              {/* User Pokemon */}
              <motion.img
                src={raceMinipicUser || "/img/placeholder.png"}
                alt="Your Pokemon"
                style={{ width: 60, height: 'auto', imageRendering: 'pixelated' }}
                initial={{ x: 0 }}
                animate={{
                  x: racingResult
                    ? racingResult.message.includes("Congrats!")
                      ? 200
                      : 50
                    : 50,
                }}
                transition={{ duration: 2 }}
                onAnimationComplete={handleAnimationComplete}
              />

              {/* Track Pokemon */}
              <motion.img
                src={raceMinipicTrack || "/img/placeholder.png"}
                alt="Track Pokemon"
                style={{ width: 60, height: 'auto', imageRendering: 'pixelated' }}
                initial={{ x: 0 }}
                animate={{
                  x: racingResult
                    ? racingResult.message.includes("Unlucky!")
                      ? -200
                      : -50
                    : -50,
                }}
                transition={{ duration: 2 }}
              />
            </div>

            {/* Race Result */}
            {racingResult && (
              <div style={{ textAlign: 'center' }}>
                <p style={{ marginBottom: '16px' }}>{racingResult.message}</p>
                <button
                  onClick={closeRacingOverlay}
                  style={{
                    background: 'rgba(108, 117, 125, 0.5)',
                    border: 'none',
                    color: '#fff',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* POPUP OVERLAY */}
      {showPopup && (
        <div 
          className="game-popup-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px 10px',
            boxSizing: 'border-box',
            zIndex: 1050
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(29, 34, 46, 0.95)',
              color: '#fff',
              borderRadius: 12,
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '400px',
              width: '90%',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: popupColor || 'rgba(0, 0, 0, 0.2)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Notification</h3>
              </div>
              <button
                onClick={() => setShowPopup(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  opacity: 0.7
                }}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.7'}
              >
                ✕
              </button>
            </div>
            
            {/* Body */}
            <div style={{
              padding: '20px',
              textAlign: 'center'
            }}>
              {popupImg && (
                <img
                  src={popupImg}
                  alt="Popup"
                  style={{
                    width: '120px',
                    margin: '0 auto 16px auto',
                    display: 'block',
                    imageRendering: 'pixelated'
                  }}
                />
              )}
              <p style={{ margin: '10px 0', fontSize: '14px' }}>{popupMessage}</p>
            </div>
            
            {/* Footer */}
            <div style={{
              padding: '12px 20px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              justifyContent: 'center',
              background: 'rgba(0, 0, 0, 0.1)'
            }}>
              <button
                onClick={() => setShowPopup(false)}
                style={{
                  background: 'rgba(220, 53, 69, 0.8)',
                  border: '1px solid rgba(220, 53, 69, 0.8)',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(220, 53, 69, 1)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(220, 53, 69, 0.8)'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RacetrackModal;
